import { ReactNode, isValidElement } from 'react';
import { createPortal } from 'react-dom';
import {
  ImageBackground,
  ImageSourcePropType,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TouchableWithoutFeedback,
  View,
  useWindowDimensions,
} from 'react-native';
import { useTheme } from 'styled-components/native';
import { Platform } from 'utils/platform';

import BG from 'assets/Splash/BgInside.png';

import FocusView from 'components/FocusView';

export type ModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
  size?: 'sm' | 'md' | 'lg' | 'full';
  modalHeader?: ReactNode;
  modalBody: ReactNode;
  modalFooter?: ReactNode;
  headerCloseButton?: boolean;
  backGroundImage?: ImageSourcePropType;
};

const renderSafeContent = (content: ReactNode) => {
  if (!content || typeof content === 'boolean') return null;

  if (typeof content === 'string' || typeof content === 'number') {
    return <Text>{content}</Text>;
  }

  if (isValidElement(content)) {
    return content;
  }

  return <View>{content}</View>;
};

export function BaseModal({
  isOpen = false,
  onClose,
  modalHeader,
  modalBody,
  modalFooter,
  backGroundImage,
  headerCloseButton = false,
  size = 'md',
}: ModalProps) {
  const theme = useTheme();
  const { height } = useWindowDimensions();

  const modalSize = (size: string) => {
    switch (size) {
      case 'sm':
        return 500;
      case 'md':
        return 600;
      case 'lg':
        return 700;
      case 'full':
        return '100%';
      default:
        return 600;
    }
  };

  if (!modalBody || !isOpen) {
    return null;
  }

  const body = (
    <>
      {headerCloseButton ? (
        <Pressable onPress={onClose} style={styles.closeButton}>
          <View style={styles.closeIcon} />
        </Pressable>
      ) : null}
      <View style={{ flex: 1 }}>
        <View style={styles.contentContainer}>
          <ImageBackground
            source={backGroundImage ?? BG}
            style={styles.imageBg}
            resizeMode="cover"
          />
          {modalHeader ? (
            <View style={styles.modalHeader}>{renderSafeContent(modalHeader)}</View>
          ) : null}
          <View style={styles.bodyWrapper}>
            <View style={styles.modalBody}>{renderSafeContent(modalBody)}</View>
          </View>
          {modalFooter ? (
            <View style={styles.modalFooter}>{renderSafeContent(modalFooter)}</View>
          ) : null}
        </View>
      </View>
    </>
  );

  const tvBody = <div className="navigation-container navigation-priority">{body}</div>;
  const tvAndroidBody = (
    <FocusView trapFocusDown trapFocusLeft trapFocusRight trapFocusUp>
      {body}
    </FocusView>
  );

  if (Platform.isWebTV && isOpen) {
    return createPortal(
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
        }}>
        <Pressable style={styles.backdrop} onPress={onClose} />
        <View style={{ flex: 1 }}>
          <View style={[styles.container, size === 'full' && { padding: 10 }]}>
            <View
              style={[
                styles.modalContent,
                {
                  backgroundColor: theme.colors.base.darkBlue,
                  ...(size === 'full' && { maxWidth: '95%', margin: 'auto' }),
                },
              ]}>
              {tvBody}
            </View>
          </View>
        </View>
      </View>,
      document.querySelector('#root')!
    );
  }

  return (
    <Modal visible={isOpen} transparent={true} animationType="fade" onRequestClose={onClose}>
      {Platform.OS === 'web' ? (
        <Pressable style={styles.backdrop} onPress={onClose} />
      ) : (
        <TouchableWithoutFeedback onPress={onClose}>
          <View style={styles.backdrop} />
        </TouchableWithoutFeedback>
      )}
      <View style={{ flex: 1 }}>
        <View style={[styles.container, size === 'full' && { padding: 10 }]}>
          {Platform.OS === 'web' ? (
            <Pressable onPress={(e) => e.stopPropagation()}>
              <View
                style={[
                  styles.modalContent,
                  {
                    backgroundColor: theme.colors.base.darkBlue,
                    ...(size === 'full' && { maxWidth: '95%', margin: 'auto' }),
                  },
                  { height: Platform.isMobileWeb() ? height - 100 : 'auto' },
                ]}>
                {Platform.isAndroidTV ? tvAndroidBody : body}
              </View>
            </Pressable>
          ) : (
            <TouchableWithoutFeedback onPress={() => {}}>
              <View
                style={[
                  styles.modalContent,
                  { backgroundColor: theme.colors.base.darkBlue, height: modalSize(size) },
                ]}>
                {Platform.isAndroidTV ? tvAndroidBody : body}
              </View>
            </TouchableWithoutFeedback>
          )}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 30,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  modalContent: {
    borderWidth: 1,
    borderRadius: 32,
    position: 'relative',
    borderColor: '#4A5568',
    backgroundColor: '#4A5568',
    maxWidth: 500,
    width: '100%',
    overflow: 'hidden',
  },
  contentContainer: {
    flex: 1,
    flexDirection: 'column',
    height: '100%',
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  modalHeader: {
    minHeight: 48,
    justifyContent: 'center',
    marginBottom: 10,
    zIndex: 2,
    backgroundColor: 'transparent',
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  bodyWrapper: {
    flex: 1,
    paddingHorizontal: 24,
    overflow: 'hidden',
    paddingBottom: 24,
  },
  modalBody: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  modalFooter: {
    minHeight: 48,
    justifyContent: 'center',
    marginTop: 10,
    backgroundColor: 'transparent',
    paddingHorizontal: 24,
    paddingBottom: 24,
    zIndex: 2,
  },
  closeButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 3,
    padding: 10,
  },
  closeIcon: {
    width: 20,
    height: 20,
    backgroundColor: '#fff',
    borderRadius: 10,
  },
  imageBg: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 0,
  },
});
